/**
 * ReminderPresetChips (Plan 03-08).
 *
 * Multi-select reminder preset chips per UI-SPEC §6 EventEditSheet "Påminnelse".
 * 4 chips: Ingen / 1 time før / Kvelden før kl 20:00 / Samme morgen kl 07:00.
 *
 * Multi-select: a member can have "1 time før" + "Kvelden før" simultaneously.
 * Selecting "Ingen" clears all other selections (mutually exclusive with the rest).
 *
 * Sentinel values (-2000, -3000) are resolved to actual lead_minutes by
 * EventEditSheet based on the event's starts_at time. These sentinels are
 * NOT exposed to the API — EventEditSheet converts them before submit.
 *
 * Values:
 *   - oneHour: 60 min (fixed)
 *   - eveningBefore: sentinel -2000 → resolved to prev-day 20:00 delta
 *   - morningOf: sentinel -3000 → resolved to same-day 07:00 delta
 *
 * Utseende (#157, steg 7b, Stell_Hendelse-skissen): chipene er `Chip`-
 * primitiven (aria-pressed), uten bjelleikon. Gruppen navngis av etiketten
 * over den (`labelledBy`). «Ingen» beholdes selv om skissen ikke har den.
 */
import { useTranslation } from 'react-i18next';
import { Chip } from '../../components/primitives';

export type ReminderPreset = 'none' | 'oneHour' | 'eveningBefore' | 'morningOf';

/**
 * Sentinel lead_minutes values.
 * EventEditSheet resolves these to actual lead_minutes before calling
 * useEventMutations. Sentinels use large negative values to distinguish
 * from any real lead_minutes (always non-negative per T-CAL-40 Zod guard).
 *
 * eveningBefore: fires at 20:00 the evening before the event.
 * morningOf: fires at 07:00 the morning of the event.
 */
export const PRESET_LEAD_MINUTES: Record<Exclude<ReminderPreset, 'none'>, number> = {
  oneHour: 60,
  eveningBefore: -2000, // sentinel — resolved per occurrence at submit
  morningOf: -3000,     // sentinel — resolved per occurrence at submit
};

export function ReminderPresetChips({
  value,
  onChange,
  labelledBy,
}: {
  value: ReminderPreset[];
  onChange: (next: ReminderPreset[]) => void;
  labelledBy?: string;
}) {
  const { t } = useTranslation();

  const all: ReminderPreset[] = ['none', 'oneHour', 'eveningBefore', 'morningOf'];

  const toggle = (p: ReminderPreset) => {
    if (p === 'none') {
      onChange(['none']);
      return;
    }
    const filtered = value.filter((v) => v !== 'none');
    if (filtered.includes(p)) {
      const next = filtered.filter((v) => v !== p);
      onChange(next.length === 0 ? ['none'] : next);
    } else {
      onChange([...filtered, p]);
    }
  };

  return (
    <div
      role="group"
      aria-labelledby={labelledBy}
      data-testid="reminder-presets"
      className="flex flex-wrap gap-2"
    >
      {all.map((p) => (
        <Chip
          key={p}
          selected={value.includes(p)}
          onClick={() => toggle(p)}
          data-testid={`reminder-${p}`}
        >
          {t(`calendar.reminder.preset.${p}`)}
        </Chip>
      ))}
    </div>
  );
}
